import { BEYOND_WORDS_SCRIPT } from "@config/beyondWords";
import { registerAnalyticsEvents } from "@elements/BeyondWordsPlayer/helper/registerAnalyticsEvents";
import { registerMediaControllerEvents } from "@elements/BeyondWordsPlayer/helper/registerMediaControllerEvents";
import { registerPlayerStyleEvents } from "@elements/BeyondWordsPlayer/helper/registerPlayerStyleEvents";
import { BEYOND_WORDS_PROJECT_ID } from "@helper/getEnvVariables";
import { useAudioAnalytics } from "@hooks/useAnalyticsCall/useAudioAnalytics";
import useScript from "@hooks/useScript/useScript";
import {
  BeyondWordsPlayerConfig,
  BeyondWordsPlayerInstance,
  BeyondWordsPlayerProps,
} from "@typings/BeyondWords";
import { useEffect, useRef } from "react";

export const useBeyondWordsPlayer = ({
  cueId,
  title,
  articleId,
  isInfiniteScrollArticle,
  visibleArticleId,
}: BeyondWordsPlayerProps) => {
  const playerRef = useRef<HTMLDivElement>(null);
  const initializedRef = useRef(false);
  const bhAudioTracking = useAudioAnalytics();
  const playerRefId = useRef(
    `beyondwords-ref-${cueId}-${Math.random().toString(36).substr(2, 9)}`,
  );

  const { isScriptReady } = useScript({
    src: BEYOND_WORDS_SCRIPT,
    id: "beyondwords-player-script",
    checkScriptIsLoaded: () =>
      typeof window !== "undefined" && !!window.BeyondWords,
  });

  useEffect(() => {
    if (!isScriptReady || !playerRef.current || initializedRef.current) return;

    initializedRef.current = true;

    // Create a "clean" div for the inline player target.
    // This native DOM element won't have React internal properties (__reactFiber$),
    // which prevents the "circular structure to JSON" error if the library stringifies its config.
    const inlineTarget = document.createElement("div");
    inlineTarget.className = "bw-inline-target";
    playerRef.current.appendChild(inlineTarget);

    // Also look for the sticky widget container and provide a clean target there if it exists.
    let cleanWidgetTarget: HTMLDivElement | null = null;
    const stickyContainer = document.getElementById(
      `tts-sticky-widget-${cueId}`,
    );
    if (stickyContainer) {
      cleanWidgetTarget = document.createElement("div");
      cleanWidgetTarget.className = "bw-sticky-target";
      stickyContainer.appendChild(cleanWidgetTarget);
    }

    const playerProps: BeyondWordsPlayerConfig = {
      target: inlineTarget,
      // unique widget target per article to avoid conflicts in infinite scroll
      widgetTarget: cleanWidgetTarget || `#tts-sticky-widget-${cueId}`,
      widgetStyle: "none",
      projectId: BEYOND_WORDS_PROJECT_ID,
      sourceId: cueId,
      playerStyle: "standard",
      clientSideEnabled: false,
      logoIconEnabled: false,
      playlistToggle: "hide",
      downloadFormats: [],
      callToAction: "Dengar Artikel ini",
      backgroundColor: "#F1F6FB",
      textColor: "#1E1E1E",
      iconColor: "#01519C",
    };

    const player: BeyondWordsPlayerInstance = new window.BeyondWords.Player(
      playerProps,
    );

    registerMediaControllerEvents(player, articleId, playerRefId.current);

    const { onClosedByUser } = registerPlayerStyleEvents(player, true);

    registerAnalyticsEvents(
      player,
      bhAudioTracking,
      () => title || "",
      () => articleId || "0",
      onClosedByUser,
    );

    const handlePauseAllPlayers = (
      event: CustomEvent<{ sourceId: string }>,
    ) => {
      if (event.detail.sourceId !== playerRefId.current) {
        if (typeof player.stop === "function") {
          player.stop();
        } else {
          player.playbackState = "paused";
        }
      }
    };

    window.addEventListener(
      "pauseAllPlayers",
      handlePauseAllPlayers as EventListener,
    );

    window.beyondWordsPlayerInstance = player;

    if (!window.beyondWordsPlayerInstances) {
      window.beyondWordsPlayerInstances = {};
    }
    window.beyondWordsPlayerInstances[cueId] = player;

    window.dispatchEvent(new CustomEvent(`beyondwords-ready-${cueId}`));

    // Sync player play/pause state with the sticky bar's visibility state.
    // This ensure that if the user starts the inline player, the sticky bar
    // reflects that the TTS is active.
    player.addEventListener("PlaybackPlaying", () => {
      window.dispatchEvent(
        new CustomEvent(`beyondwords-visibility-${cueId}`, {
          detail: { visible: true },
        }),
      );
    });

    const handlePlayerStop = () => {
      window.dispatchEvent(
        new CustomEvent(`beyondwords-visibility-${cueId}`, {
          detail: { visible: false },
        }),
      );
    };

    player.addEventListener("PlaybackPaused", handlePlayerStop);
    player.addEventListener("PlaybackEnded", handlePlayerStop);

    const setPlaybackForVisible = (visible: boolean) => {
      try {
        if (visible) {
          player.widgetStyle = "standard"; // Ensure widget is shown when visible
          if (typeof player.play === "function") {
            // prefer explicit play API
            player.play();
          } else {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            player.playbackState = "playing";
          }
          // Notify other players to pause
          window.dispatchEvent(
            new CustomEvent("pauseAllPlayers", {
              detail: { sourceId: playerRefId.current },
            }),
          );
        } else {
          // Only hide widget if it's not closed by user to avoid clobbering close button state
          if (player.widgetStyle !== "closed-by-user") {
            player.widgetStyle = "none";
          }
          if (typeof player.stop === "function") {
            player.stop();
          } else {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            player.playbackState = "paused";
          }
        }
        // Dispatch a local event so the StickyBar UI knows this player became visible/hidden
        window.dispatchEvent(
          new CustomEvent(`beyondwords-visibility-${cueId}`, {
            detail: { visible },
          }),
        );
      } catch {
        // swallow errors to avoid breaking player init
      }
    };

    const handleToggle = (e: Event) => {
      const customEvent = e as CustomEvent<{ visible: boolean }>;
      setPlaybackForVisible(customEvent.detail.visible);
    };

    window.addEventListener(
      `toggle-tts-${cueId}`,
      handleToggle as EventListener,
    );

    // ensure observers are cleaned up when player is destroyed
    const origDestroy = player.destroy.bind(player);
    player.destroy = function (
      this: BeyondWordsPlayerInstance,
      ...args: unknown[]
    ) {
      try {
        if (window.beyondWordsPlayerInstances) {
          window.beyondWordsPlayerInstances[cueId] = undefined;
        }
        window.removeEventListener(
          `toggle-tts-${cueId}`,
          handleToggle as EventListener,
        );
      } catch {
        /* ignore */
      }
      origDestroy(...args);
    };

    return () => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      player.destroy();
      inlineTarget.remove();
      cleanWidgetTarget?.remove();
      window.removeEventListener(
        "pauseAllPlayers",
        handlePauseAllPlayers as EventListener,
      );
    };
  }, [
    isScriptReady,
    cueId,
    title,
    articleId,
    bhAudioTracking,
    playerRef,
    initializedRef,
  ]);

  // Handle sticky player handover at article boundaries
  useEffect(() => {
    if (
      !visibleArticleId ||
      !initializedRef.current ||
      visibleArticleId !== cueId
    ) {
      return;
    }

    const currentPlayer = window.beyondWordsPlayerInstances?.[cueId];
    if (!currentPlayer) return;

    // This player's article is now the visible/active one.
    // Check if another player is currently playing to trigger handover.
    const otherPlayingId = Object.keys(
      window.beyondWordsPlayerInstances || {},
    ).find(
      (id) =>
        id !== cueId &&
        window.beyondWordsPlayerInstances?.[id]?.playbackState === "playing",
    );

    if (otherPlayingId && window.beyondWordsPlayerInstances) {
      const otherPlayer = window.beyondWordsPlayerInstances[otherPlayingId];
      if (otherPlayer) {
        // Sync styles: hide old, show new
        if (typeof otherPlayer.stop === "function") {
          otherPlayer.stop();
        } else {
          otherPlayer.playbackState = "paused";
        }
        otherPlayer.widgetStyle = "none";
        // notify old UI
        window.dispatchEvent(
          new CustomEvent(`beyondwords-visibility-${otherPlayingId}`, {
            detail: { visible: false },
          }),
        );
      }

      if (typeof currentPlayer.play === "function") {
        currentPlayer.play();
      } else {
        currentPlayer.playbackState = "playing";
      }
      currentPlayer.widgetStyle = "standard";

      // Notify other players (like Brightcove) via mediaController event
      window.dispatchEvent(
        new CustomEvent("pauseAllPlayers", {
          detail: { sourceId: playerRefId.current },
        }),
      );
    }
  }, [visibleArticleId, cueId, isInfiniteScrollArticle]);

  return playerRef;
};
